import type { ValidationFieldCell } from '../../../helpers/deviceValidation';
import type { AthleteAssignmentReportRow } from '../../shared/types/ValidationReportTypes';
import type { AthleteCsvRow } from './athleteAssignmentsCsvParse';
import type { EventDeviceRow } from '../types';

export function buildAthleteAssignmentReportRows(
  athletes: AthleteCsvRow[],
  assignments: EventDeviceRow[],
): AthleteAssignmentReportRow[] {
  const rearByAthleteId = new Map<string, string>();
  const frontByAthleteId = new Map<string, string>();
  for (const row of assignments) {
    const athleteId = String(row.assignedAthleteId ?? '').trim();
    const deviceId = String(row.deviceId ?? '').trim();
    if (!athleteId || !deviceId) continue;
    if (/^front_/i.test(deviceId)) {
      frontByAthleteId.set(athleteId, deviceId);
      continue;
    }
    if (/^rear_/i.test(deviceId)) {
      rearByAthleteId.set(athleteId, deviceId);
      continue;
    }
    // Fallback for any non-standard IDs: treat as rear for compatibility.
    if (!rearByAthleteId.has(athleteId)) {
      rearByAthleteId.set(athleteId, deviceId);
    }
  }

  const report = athletes.map((athlete) => {
    const rearDeviceId = rearByAthleteId.get(athlete.athleteId) ?? '';
    const frontDeviceId = frontByAthleteId.get(athlete.athleteId) ?? '';
    const rearAssigned = !!rearDeviceId.trim();
    const frontAssigned = !!frontDeviceId.trim();
    const rear: ValidationFieldCell = rearAssigned
      ? { result: 'pass', detail: '' }
      : { result: 'fail', detail: 'Rear device not assigned' };
    const front: ValidationFieldCell = frontAssigned
      ? { result: 'pass', detail: '' }
      : { result: 'fail', detail: 'Front device not assigned' };
    const notes = [rear.detail, front.detail].filter(Boolean).join('; ');
    return {
      athleteName: athlete.athleteName,
      athleteId: athlete.athleteId,
      athleteRaceNo: athlete.athleteRaceNo,
      rearDeviceId,
      frontDeviceId,
      rear,
      front,
      notes,
    };
  });

  report.sort((a, b) => {
    const aFailCount = (a.rear.result === 'fail' ? 1 : 0) + (a.front.result === 'fail' ? 1 : 0);
    const bFailCount = (b.rear.result === 'fail' ? 1 : 0) + (b.front.result === 'fail' ? 1 : 0);
    if (aFailCount !== bFailCount) return bFailCount - aFailCount;
    return a.athleteRaceNo.localeCompare(b.athleteRaceNo, undefined, { numeric: true });
  });
  return report;
}

function toCsvCell(value: string): string {
  const safe = String(value ?? '').replace(/"/g, '""');
  return `"${safe}"`;
}

function csvResult(cell: ValidationFieldCell): string {
  if (cell.result === 'pass') return 'pass';
  if (cell.result === 'fail') return 'fail';
  return 'n/a';
}

/** CSV matching AthleteAssignmentReportTable columns. */
export function buildAthleteAssignmentReportCsv(params: {
  eventId: string;
  raceId: string;
  rows: AthleteAssignmentReportRow[];
}): string {
  const { eventId, raceId, rows } = params;
  const header = [
    'event_id',
    'race_id',
    'athlete_name',
    'athlete_id',
    'athlete_race_no',
    'rear_device_id',
    'front_device_id',
    'rear_assigned',
    'front_assigned',
    'notes',
  ].join(',');
  const lines = rows.map((row) =>
    [
      toCsvCell(eventId),
      toCsvCell(raceId),
      toCsvCell(row.athleteName),
      toCsvCell(row.athleteId),
      toCsvCell(row.athleteRaceNo),
      toCsvCell(row.rearDeviceId),
      toCsvCell(row.frontDeviceId),
      toCsvCell(csvResult(row.rear)),
      toCsvCell(csvResult(row.front)),
      toCsvCell(row.notes),
    ].join(','),
  );
  return [header, ...lines].join('\n');
}

export function athleteMatchesTableFilter(
  row: Pick<AthleteAssignmentReportRow, 'athleteName' | 'athleteId' | 'athleteRaceNo'>,
  filter: string,
): boolean {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  return [row.athleteName, row.athleteId, row.athleteRaceNo].some((v) =>
    String(v ?? '')
      .toLowerCase()
      .includes(q),
  );
}
